import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { BuildingIcon, UserIcon, MapPinIcon, PhoneIcon, MailIcon, CalendarIcon, TagIcon, QrCodeIcon, LayoutDashboardIcon, ShoppingCartIcon, CreditCardIcon, ReceiptIcon, SettingsIcon, ArrowLeftIcon, EditIcon, RefreshCwIcon, AlertCircleIcon, CheckCircleIcon, XCircleIcon, ClockIcon, HistoryIcon, ChevronRightIcon, PlusIcon } from 'lucide-react';
import Card from '../../components/ui/Card';
import Table from '../../components/ui/Table';
import StatusBadge from '../../components/ui/StatusBadge';
import Modal from '../../components/ui/Modal';
// Mock data for merchant details
const merchantData = {
  id: 1,
  name: 'ABC Corporation',
  businessType: 'Retail',
  status: 'active',
  region: 'North',
  onboardingDate: '2023-05-15',
  lastActivityDate: '2023-10-25',
  aggregator: null,
  contactPerson: 'John Smith',
  email: 'john@abccorp.com',
  phone: '+1 (555) 123-4567',
  address: '123 Main St, Suite 200, New York, NY 10001',
  taxId: 'TAX12345678',
  registrationNumber: 'REG87654321',
  businessCategory: 'Department Store',
  accountManager: 'Sarah Johnson',
  paymentMethods: ['Credit Card', 'Debit Card', 'Mobile Wallet'],
  commissionRate: '2.5%',
  settlementCycle: 'Daily',
  bankAccount: {
    accountName: 'ABC Corporation',
    accountNumber: '****5678',
    bankName: 'First National Bank',
    branchCode: 'FNB001'
  },
  statusHistory: [{
    previousStatus: 'pending',
    newStatus: 'active',
    changedBy: 'admin@example.com',
    changedAt: '2023-05-20T10:30:00Z',
    reason: 'All requirements met'
  }, {
    previousStatus: null,
    newStatus: 'pending',
    changedBy: 'system',
    changedAt: '2023-05-15T08:15:00Z',
    reason: 'Initial application submitted'
  }]
};
// Mock data for transactions
const transactionsData = [{
  id: 'TXN123456',
  date: '2023-10-25T14:30:00Z',
  amount: 125.99,
  status: 'completed',
  customer: 'Customer A',
  paymentMethod: 'Credit Card',
  reference: 'REF123456'
}, {
  id: 'TXN123457',
  date: '2023-10-24T11:20:00Z',
  amount: 75.5,
  status: 'completed',
  customer: 'Customer B',
  paymentMethod: 'Debit Card',
  reference: 'REF123457'
}, {
  id: 'TXN123458',
  date: '2023-10-23T16:45:00Z',
  amount: 230.0,
  status: 'completed',
  customer: 'Customer C',
  paymentMethod: 'Mobile Wallet',
  reference: 'REF123458'
}, {
  id: 'TXN123459',
  date: '2023-10-22T09:15:00Z',
  amount: 45.75,
  status: 'refunded',
  customer: 'Customer D',
  paymentMethod: 'Credit Card',
  reference: 'REF123459'
}, {
  id: 'TXN123460',
  date: '2023-10-21T13:10:00Z',
  amount: 180.25,
  status: 'completed',
  customer: 'Customer E',
  paymentMethod: 'Debit Card',
  reference: 'REF123460'
}];
// Mock data for QR codes
const qrCodesData = [{
  id: 'QR001',
  name: 'Main Store Checkout',
  type: 'Static',
  createdDate: '2023-06-01T10:00:00Z',
  status: 'active',
  scans: 1245,
  lastScan: '2023-10-25T15:30:00Z'
}, {
  id: 'QR002',
  name: 'Online Store',
  type: 'Dynamic',
  createdDate: '2023-06-15T11:30:00Z',
  status: 'active',
  scans: 2780,
  lastScan: '2023-10-25T16:45:00Z'
}, {
  id: 'QR003',
  name: 'Promotional Campaign',
  type: 'Dynamic',
  createdDate: '2023-08-10T09:15:00Z',
  status: 'inactive',
  scans: 567,
  lastScan: '2023-09-30T12:20:00Z'
}];
// Mock data for tills
const tillsData = [{
  id: 'TILL001',
  name: 'Checkout 1',
  location: 'Main Floor',
  status: 'active',
  lastActivity: '2023-10-25T16:30:00Z',
  transactions: 1876
}, {
  id: 'TILL002',
  name: 'Checkout 2',
  location: 'Main Floor',
  status: 'active',
  lastActivity: '2023-10-25T16:15:00Z',
  transactions: 1543
}, {
  id: 'TILL003',
  name: 'Customer Service',
  location: 'Second Floor',
  status: 'inactive',
  lastActivity: '2023-10-20T14:45:00Z',
  transactions: 432
}];
// Type definitions
interface StatusHistoryItem {
  previousStatus: string | null;
  newStatus: string;
  changedBy: string;
  changedAt: string;
  reason: string;
}
interface BankAccount {
  accountName: string;
  accountNumber: string;
  bankName: string;
  branchCode: string;
}
interface Merchant {
  id: number;
  name: string;
  businessType: string;
  status: string;
  region: string;
  onboardingDate: string;
  lastActivityDate: string;
  aggregator: string | null;
  contactPerson: string;
  email: string;
  phone: string;
  address: string;
  taxId: string;
  registrationNumber: string;
  businessCategory: string;
  accountManager: string;
  paymentMethods: string[];
  commissionRate: string;
  settlementCycle: string;
  bankAccount: BankAccount;
  statusHistory: StatusHistoryItem[];
}
interface Transaction {
  id: string;
  date: string;
  amount: number;
  status: string;
  customer: string;
  paymentMethod: string;
  reference: string;
}
interface QRCode {
  id: string;
  name: string;
  type: string;
  createdDate: string;
  status: string;
  scans: number;
  lastScan: string;
}
interface Till {
  id: string;
  name: string;
  location: string;
  status: string;
  lastActivity: string;
  transactions: number;
}
type MerchantStatus = 'active' | 'inactive' | 'pending' | 'suspended' | string;
const MerchantDetails: React.FC = () => {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const [merchant, setMerchant] = useState<Merchant | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [qrCodes, setQrCodes] = useState<QRCode[]>([]);
  const [tills, setTills] = useState<Till[]>([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showStatusHistoryModal, setShowStatusHistoryModal] = useState(false);
  const [newStatus, setNewStatus] = useState<MerchantStatus>('');
  const [statusChangeReason, setStatusChangeReason] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    // Simulate API call to fetch merchant data
    setTimeout(() => {
      setMerchant(merchantData);
      setTransactions(transactionsData);
      setQrCodes(qrCodesData);
      setTills(tillsData);
      setIsLoading(false);
    }, 800);
  }, [id]);
  const handleChangeStatus = () => {
    if (!merchant) return;
    setNewStatus(merchant.status);
    setShowStatusModal(true);
  };
  const confirmStatusChange = () => {
    if (!merchant || !newStatus || newStatus === merchant.status) {
      setShowStatusModal(false);
      return;
    }
    // Update merchant status and add to history
    const statusHistoryItem: StatusHistoryItem = {
      previousStatus: merchant.status,
      newStatus: newStatus,
      changedBy: 'current.user@example.com',
      changedAt: new Date().toISOString(),
      reason: statusChangeReason
    };
    setMerchant({
      ...merchant,
      status: newStatus,
      statusHistory: [statusHistoryItem, ...merchant.statusHistory]
    });
    setShowStatusModal(false);
    setStatusChangeReason('');
    setNewStatus('');
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <StatusBadge status="success" text="Active" data-id="element-5109" />;
      case 'inactive':
        return <StatusBadge status="danger" text="Inactive" data-id="element-5110" />;
      case 'pending':
        return <StatusBadge status="warning" text="Pending" data-id="element-5111" />;
      case 'suspended':
        return <StatusBadge status="info" text="Suspended" data-id="element-5112" />;
      case 'completed':
        return <StatusBadge status="success" text="Completed" data-id="element-5113" />;
      case 'refunded':
        return <StatusBadge status="danger" text="Refunded" data-id="element-5114" />;
      default:
        return <StatusBadge status="default" text={status} data-id="element-5115" />;
    }
  };
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" data-id="element-5116" />;
      case 'inactive':
        return <XCircleIcon className="h-5 w-5 text-red-500" data-id="element-5117" />;
      case 'pending':
        return <ClockIcon className="h-5 w-5 text-yellow-500" data-id="element-5118" />;
      case 'suspended':
        return <AlertCircleIcon className="h-5 w-5 text-blue-500" data-id="element-5119" />;
      default:
        return null;
    }
  };
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };
  const formatDateTime = (dateTimeString: string) => {
    const date = new Date(dateTimeString);
    return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
  };
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-PK', {
      style: 'currency',
      currency: 'PKR'
    }).format(amount);
  };
  if (isLoading) {
    return <div className="flex items-center justify-center min-h-[60vh]" data-id="element-5120">
        <div className="text-center" data-id="element-5121">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" data-id="element-5122"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400" data-id="element-5123">
            Loading merchant details...
          </p>
        </div>
      </div>;
  }
  if (!merchant) {
    return <div className="flex flex-col items-center justify-center min-h-[60vh]" data-id="element-5124">
        <AlertCircleIcon className="h-12 w-12 text-red-500 mb-4" data-id="element-5125" />
        <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-2" data-id="element-5126">
          Merchant Not Found
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mb-6" data-id="element-5127">
          The merchant you're looking for doesn't exist or has been removed.
        </p>
        <button onClick={() => navigate('/merchants')} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors" data-id="element-5128">
          Back to Merchant List
        </button>
      </div>;
  }
  const renderOverviewTab = () => <div className="space-y-6" data-id="element-5129">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-5130">
        {/* Merchant Profile */}
        <Card className="lg:col-span-2" data-id="element-5131">
          <div className="flex justify-between items-start mb-4" data-id="element-5132">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center" data-id="element-5133">
              <BuildingIcon className="h-5 w-5 mr-2 text-blue-500" data-id="element-5134" />
              Business Information
            </h3>
            <Link to={`/merchants/edit/${merchant.id}`} className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 flex items-center text-sm" data-id="element-5135">
              <EditIcon className="h-4 w-4 mr-1" data-id="element-5136" />
              Edit
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4" data-id="element-5137">
            <div data-id="element-5138">
              <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-5139">
                Business Name
              </h4>
              <p className="mt-1 text-gray-900 dark:text-white" data-id="element-5140">
                {merchant.name}
              </p>
            </div>
            <div data-id="element-5141">
              <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-5142">
                Business Type
              </h4>
              <p className="mt-1 text-gray-900 dark:text-white" data-id="element-5143">
                {merchant.businessType}
              </p>
            </div>
            <div data-id="element-5144">
              <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-5145">
                Business Category
              </h4>
              <p className="mt-1 text-gray-900 dark:text-white" data-id="element-5146">
                {merchant.businessCategory}
              </p>
            </div>
            <div data-id="element-5147">
              <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-5148">
                Region
              </h4>
              <p className="mt-1 text-gray-900 dark:text-white" data-id="element-5149">
                {merchant.region}
              </p>
            </div>
            <div data-id="element-5150">
              <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-5151">
                Tax ID
              </h4>
              <p className="mt-1 text-gray-900 dark:text-white" data-id="element-5152">
                {merchant.taxId}
              </p>
            </div>
            <div data-id="element-5153">
              <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-5154">
                Registration Number
              </h4>
              <p className="mt-1 text-gray-900 dark:text-white" data-id="element-5155">
                {merchant.registrationNumber}
              </p>
            </div>
            <div data-id="element-5156">
              <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-5157">
                Address
              </h4>
              <p className="mt-1 text-gray-900 dark:text-white" data-id="element-5158">
                {merchant.address}
              </p>
            </div>
            <div data-id="element-5159">
              <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-5160">
                Aggregator
              </h4>
              <p className="mt-1 text-gray-900 dark:text-white" data-id="element-5161">
                {merchant.aggregator || 'N/A'}
              </p>
            </div>
          </div>
        </Card>
        {/* Status Card */}
        <Card data-id="element-5162">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center" data-id="element-5163">
            <TagIcon className="h-5 w-5 mr-2 text-blue-500" data-id="element-5164" />
            Merchant Status
          </h3>
          <div className="flex flex-col items-center justify-center p-4 bg-gray-50 dark:bg-gray-800 rounded-lg" data-id="element-5165">
            <div className="p-3 bg-white dark:bg-gray-700 rounded-full mb-3 shadow-sm" data-id="element-5166">
              {getStatusIcon(merchant.status)}
            </div>
            <div className="text-center" data-id="element-5167">
              <div className="mb-2" data-id="element-5168">{getStatusBadge(merchant.status)}</div>
              <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-5169">
                Last Updated:{' '}
                {merchant.statusHistory.length > 0 ? formatDateTime(merchant.statusHistory[0].changedAt) : 'N/A'}
              </p>
            </div>
          </div>
          <div className="mt-4 space-y-3" data-id="element-5170">
            <button onClick={handleChangeStatus} className="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" data-id="element-5171">
              <RefreshCwIcon className="h-4 w-4 mr-2" data-id="element-5172" />
              Change Status
            </button>
            <button onClick={() => setShowStatusHistoryModal(true)} className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-5173">
              <HistoryIcon className="h-4 w-4 mr-2" data-id="element-5174" />
              View Status History
            </button>
          </div>
        </Card>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-5175">
        {/* Contact Information */}
        <Card data-id="element-5176">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center" data-id="element-5177">
            <UserIcon className="h-5 w-5 mr-2 text-blue-500" data-id="element-5178" />
            Contact Information
          </h3>
          <div className="space-y-4" data-id="element-5179">
            <div className="flex items-start" data-id="element-5180">
              <UserIcon className="h-5 w-5 text-gray-500 dark:text-gray-400 mt-0.5 mr-3" data-id="element-5181" />
              <div data-id="element-5182">
                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-5183">
                  Contact Person
                </h4>
                <p className="mt-1 text-gray-900 dark:text-white" data-id="element-5184">
                  {merchant.contactPerson}
                </p>
              </div>
            </div>
            <div className="flex items-start" data-id="element-5185">
              <MailIcon className="h-5 w-5 text-gray-500 dark:text-gray-400 mt-0.5 mr-3" data-id="element-5186" />
              <div data-id="element-5187">
                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-5188">
                  Email
                </h4>
                <p className="mt-1 text-gray-900 dark:text-white" data-id="element-5189">
                  {merchant.email}
                </p>
              </div>
            </div>
            <div className="flex items-start" data-id="element-5190">
              <PhoneIcon className="h-5 w-5 text-gray-500 dark:text-gray-400 mt-0.5 mr-3" data-id="element-5191" />
              <div data-id="element-5192">
                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-5193">
                  Phone
                </h4>
                <p className="mt-1 text-gray-900 dark:text-white" data-id="element-5194">
                  {merchant.phone}
                </p>
              </div>
            </div>
            <div className="flex items-start" data-id="element-5195">
              <MapPinIcon className="h-5 w-5 text-gray-500 dark:text-gray-400 mt-0.5 mr-3" data-id="element-5196" />
              <div data-id="element-5197">
                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-5198">
                  Address
                </h4>
                <p className="mt-1 text-gray-900 dark:text-white" data-id="element-5199">
                  {merchant.address}
                </p>
              </div>
            </div>
          </div>
        </Card>
        {/* Account Details */}
        <Card data-id="element-5200">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center" data-id="element-5201">
            <CreditCardIcon className="h-5 w-5 mr-2 text-blue-500" data-id="element-5202" />
            Account Details
          </h3>
          <div className="space-y-4" data-id="element-5203">
            <div data-id="element-5204">
              <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-5205">
                Account Manager
              </h4>
              <p className="mt-1 text-gray-900 dark:text-white" data-id="element-5206">
                {merchant.accountManager}
              </p>
            </div>
            <div data-id="element-5207">
              <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-5208">
                Commission Rate
              </h4>
              <p className="mt-1 text-gray-900 dark:text-white" data-id="element-5209">
                {merchant.commissionRate}
              </p>
            </div>
            <div data-id="element-5210">
              <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-5211">
                Settlement Cycle
              </h4>
              <p className="mt-1 text-gray-900 dark:text-white" data-id="element-5212">
                {merchant.settlementCycle}
              </p>
            </div>
            <div data-id="element-5213">
              <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-5214">
                Onboarding Date
              </h4>
              <p className="mt-1 text-gray-900 dark:text-white" data-id="element-5215">
                {formatDate(merchant.onboardingDate)}
              </p>
            </div>
          </div>
        </Card>
        {/* Bank Information */}
        <Card data-id="element-5216">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center" data-id="element-5217">
            <BuildingIcon className="h-5 w-5 mr-2 text-blue-500" data-id="element-5218" />
            Bank Information
          </h3>
          <div className="space-y-4" data-id="element-5219">
            <div data-id="element-5220">
              <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-5221">
                Account Name
              </h4>
              <p className="mt-1 text-gray-900 dark:text-white" data-id="element-5222">
                {merchant.bankAccount.accountName}
              </p>
            </div>
            <div data-id="element-5223">
              <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-5224">
                Account Number
              </h4>
              <p className="mt-1 text-gray-900 dark:text-white" data-id="element-5225">
                {merchant.bankAccount.accountNumber}
              </p>
            </div>
            <div data-id="element-5226">
              <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-5227">
                Bank Name
              </h4>
              <p className="mt-1 text-gray-900 dark:text-white" data-id="element-5228">
                {merchant.bankAccount.bankName}
              </p>
            </div>
            <div data-id="element-5229">
              <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-5230">
                Branch Code
              </h4>
              <p className="mt-1 text-gray-900 dark:text-white" data-id="element-5231">
                {merchant.bankAccount.branchCode}
              </p>
            </div>
          </div>
        </Card>
      </div>
    </div>;
  const renderTransactionsTab = () => <Card data-id="element-5232">
      <div className="flex justify-between items-center mb-6" data-id="element-5233">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center" data-id="element-5234">
          <ShoppingCartIcon className="h-5 w-5 mr-2 text-blue-500" data-id="element-5235" />
          Recent Transactions
        </h3>
        <div className="flex space-x-2" data-id="element-5236">
          <button className="px-3 py-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md text-sm flex items-center" data-id="element-5237">
            <FilterIcon className="h-4 w-4 mr-1" data-id="element-5238" />
            Filter
          </button>
          <Link to="/reports" className="px-3 py-1 bg-blue-600 text-white rounded-md text-sm flex items-center" data-id="element-5239">
            View All
          </Link>
        </div>
      </div>
      <Table columns={[{
      header: 'Transaction ID',
      accessor: 'id',
      sortable: true
    }, {
      header: 'Date',
      accessor: (transaction: Transaction) => formatDateTime(transaction.date),
      sortable: true
    }, {
      header: 'Amount',
      accessor: (transaction: Transaction) => formatCurrency(transaction.amount),
      sortable: true,
      className: 'text-right'
    }, {
      header: 'Status',
      accessor: (transaction: Transaction) => getStatusBadge(transaction.status),
      sortable: false
    }, {
      header: 'Customer',
      accessor: 'customer',
      sortable: true
    }, {
      header: 'Payment Method',
      accessor: 'paymentMethod',
      sortable: true
    }]} data={transactions} keyField="id" actions={transaction => <div className="flex justify-end space-x-2" data-id="element-5241">
            <button className="p-1 rounded-full text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700" data-id="element-5242">
              <ReceiptIcon className="h-5 w-5" data-id="element-5243" />
            </button>
          </div>} data-id="element-5240" />
    </Card>;
  const renderQRCodesTab = () => <Card data-id="element-5244">
      <div className="flex justify-between items-center mb-6" data-id="element-5245">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center" data-id="element-5246">
          <QrCodeIcon className="h-5 w-5 mr-2 text-blue-500" data-id="element-5247" />
          QR Codes
        </h3>
        <div className="flex space-x-2" data-id="element-5248">
          <Link to="/qrc-generator" className="px-3 py-1 bg-blue-600 text-white rounded-md text-sm flex items-center" data-id="element-5249">
            <PlusIcon className="h-4 w-4 mr-1" data-id="element-5250" />
            Generate New QR
          </Link>
        </div>
      </div>
      <Table columns={[{
      header: 'QR Name',
      accessor: 'name',
      sortable: true
    }, {
      header: 'Type',
      accessor: 'type',
      sortable: true
    }, {
      header: 'Created Date',
      accessor: (qr: QRCode) => formatDate(qr.createdDate),
      sortable: true
    }, {
      header: 'Status',
      accessor: (qr: QRCode) => getStatusBadge(qr.status),
      sortable: false
    }, {
      header: 'Total Scans',
      accessor: 'scans',
      sortable: true,
      className: 'text-right'
    }, {
      header: 'Last Scan',
      accessor: (qr: QRCode) => formatDateTime(qr.lastScan),
      sortable: true
    }]} data={qrCodes} keyField="id" actions={qr => <div className="flex justify-end space-x-2" data-id="element-5252">
            <button className="p-1 rounded-full text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30" data-id="element-5253">
              <QrCodeIcon className="h-5 w-5" data-id="element-5254" />
            </button>
            <button className="p-1 rounded-full text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700" data-id="element-5255">
              <EditIcon className="h-5 w-5" data-id="element-5256" />
            </button>
          </div>} data-id="element-5251" />
    </Card>;
  const renderTillsTab = () => <Card data-id="element-5257">
      <div className="flex justify-between items-center mb-6" data-id="element-5258">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center" data-id="element-5259">
          <LayoutDashboardIcon className="h-5 w-5 mr-2 text-blue-500" data-id="element-5260" />
          Tills
        </h3>
        <div className="flex space-x-2" data-id="element-5261">
          <Link to="/tills/new" className="px-3 py-1 bg-blue-600 text-white rounded-md text-sm flex items-center" data-id="element-5262">
            <PlusIcon className="h-4 w-4 mr-1" data-id="element-5263" />
            Add New Till
          </Link>
        </div>
      </div>
      <Table columns={[{
      header: 'Till ID',
      accessor: 'id',
      sortable: true
    }, {
      header: 'Name',
      accessor: 'name',
      sortable: true
    }, {
      header: 'Location',
      accessor: 'location',
      sortable: true
    }, {
      header: 'Status',
      accessor: (till: Till) => getStatusBadge(till.status),
      sortable: false
    }, {
      header: 'Last Activity',
      accessor: (till: Till) => formatDateTime(till.lastActivity),
      sortable: true
    }, {
      header: 'Transactions',
      accessor: 'transactions',
      sortable: true,
      className: 'text-right'
    }]} data={tills} keyField="id" actions={till => <div className="flex justify-end space-x-2" data-id="element-5265">
            <button className="p-1 rounded-full text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30" data-id="element-5266">
              <EditIcon className="h-5 w-5" data-id="element-5267" />
            </button>
          </div>} data-id="element-5264" />
    </Card>;
  return <div className="space-y-6" data-id="element-5268">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between" data-id="element-5269">
        <div className="flex items-center mb-4 md:mb-0" data-id="element-5270">
          <button onClick={() => navigate('/merchants')} className="mr-4 p-2 rounded-full text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700" data-id="element-5271">
            <ArrowLeftIcon className="h-5 w-5" data-id="element-5272" />
          </button>
          <div data-id="element-5273">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center" data-id="element-5274">
              {merchant.name}
              <span className="ml-3" data-id="element-5275">{getStatusBadge(merchant.status)}</span>
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-5276">
              {merchant.businessType} • ID: {merchant.id} • Onboarded:{' '}
              {formatDate(merchant.onboardingDate)}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2" data-id="element-5277">
          <Link to={`/merchants/edit/${merchant.id}`} className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-5278">
            <EditIcon className="h-4 w-4 mr-2" data-id="element-5279" />
            Edit
          </Link>
          <button onClick={handleChangeStatus} className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" data-id="element-5280">
            <RefreshCwIcon className="h-4 w-4 mr-2" data-id="element-5281" />
            Change Status
          </button>
        </div>
      </div>
      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700" data-id="element-5282">
        <nav className="-mb-px flex space-x-8 overflow-x-auto" data-id="element-5283">
          <button className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'overview' ? 'border-blue-500 text-blue-600 dark:text-blue-400' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:border-gray-600'}`} onClick={() => setActiveTab('overview')} data-id="element-5284">
            <LayoutDashboardIcon className="h-5 w-5 inline-block mr-2" data-id="element-5285" />
            Overview
          </button>
          <button className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'transactions' ? 'border-blue-500 text-blue-600 dark:text-blue-400' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:border-gray-600'}`} onClick={() => setActiveTab('transactions')} data-id="element-5286">
            <ShoppingCartIcon className="h-5 w-5 inline-block mr-2" data-id="element-5287" />
            Transactions
          </button>
          <button className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'qrcodes' ? 'border-blue-500 text-blue-600 dark:text-blue-400' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:border-gray-600'}`} onClick={() => setActiveTab('qrcodes')} data-id="element-5288">
            <QrCodeIcon className="h-5 w-5 inline-block mr-2" data-id="element-5289" />
            QR Codes
          </button>
          <button className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'tills' ? 'border-blue-500 text-blue-600 dark:text-blue-400' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:border-gray-600'}`} onClick={() => setActiveTab('tills')} data-id="element-5290">
            <LayoutDashboardIcon className="h-5 w-5 inline-block mr-2" data-id="element-5291" />
            Tills
          </button>
          <button className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'settings' ? 'border-blue-500 text-blue-600 dark:text-blue-400' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:border-gray-600'}`} onClick={() => setActiveTab('settings')} data-id="element-5292">
            <SettingsIcon className="h-5 w-5 inline-block mr-2" data-id="element-5293" />
            Settings
          </button>
        </nav>
      </div>
      {/* Tab Content */}
      <div data-id="element-5294">
        {activeTab === 'overview' && renderOverviewTab()}
        {activeTab === 'transactions' && renderTransactionsTab()}
        {activeTab === 'qrcodes' && renderQRCodesTab()}
        {activeTab === 'tills' && renderTillsTab()}
        {activeTab === 'settings' && <div className="flex items-center justify-center min-h-[40vh]" data-id="element-5295">
            <div className="text-center" data-id="element-5296">
              <SettingsIcon className="h-12 w-12 text-gray-400 dark:text-gray-600 mx-auto mb-4" data-id="element-5297" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2" data-id="element-5298">
                Merchant Settings
              </h3>
              <p className="text-gray-500 dark:text-gray-400 max-w-md" data-id="element-5299">
                This feature is coming soon. You'll be able to configure
                merchant-specific settings here.
              </p>
            </div>
          </div>}
      </div>
      {/* Change Status Modal */}
      <Modal isOpen={showStatusModal} onClose={() => setShowStatusModal(false)} title="Change Merchant Status" footer={<>
            <button onClick={() => setShowStatusModal(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-5301">
              Cancel
            </button>
            <button onClick={confirmStatusChange} className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" disabled={!newStatus || merchant && newStatus === merchant.status} data-id="element-5302">
              Update Status
            </button>
          </>} data-id="element-5300">
        <div className="space-y-4" data-id="element-5303">
          <div className="flex items-center mb-4" data-id="element-5304">
            <div className="p-2 rounded-full bg-gray-100 dark:bg-gray-700 mr-3" data-id="element-5305">
              <BuildingIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" data-id="element-5306" />
            </div>
            <div data-id="element-5307">
              <h3 className="text-lg font-medium" data-id="element-5308">{merchant.name}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-5309">
                Current Status: {getStatusBadge(merchant.status)}
              </p>
            </div>
          </div>
          <div data-id="element-5310">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-5311">
              New Status
            </label>
            <select value={newStatus} onChange={e => setNewStatus(e.target.value as MerchantStatus)} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" required data-id="element-5312">
              <option value="" data-id="element-5313">Select Status</option>
              <option value="active" data-id="element-5314">Active</option>
              <option value="inactive" data-id="element-5315">Inactive</option>
              <option value="pending" data-id="element-5316">Pending</option>
              <option value="suspended" data-id="element-5317">Suspended</option>
            </select>
          </div>
          <div data-id="element-5318">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-5319">
              Reason for Status Change
            </label>
            <textarea value={statusChangeReason} onChange={e => setStatusChangeReason(e.target.value)} rows={3} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="Enter reason for status change" required data-id="element-5320" />
          </div>
          <div className="bg-yellow-50 dark:bg-yellow-900/10 border border-yellow-200 dark:border-yellow-800 rounded p-3" data-id="element-5321">
            <p className="text-sm text-yellow-800 dark:text-yellow-300 flex items-start" data-id="element-5322">
              <AlertCircleIcon className="h-5 w-5 mr-2 flex-shrink-0" data-id="element-5323" />
              <span data-id="element-5324">
                <strong data-id="element-5325">Note:</strong> Changing a merchant's status may affect
                their ability to process transactions. This action will be
                recorded in the audit log.
              </span>
            </p>
          </div>
        </div>
      </Modal>
      {/* Status History Modal */}
      <Modal isOpen={showStatusHistoryModal} onClose={() => setShowStatusHistoryModal(false)} title="Status Change History" size="lg" data-id="element-5326">
        <div className="space-y-4" data-id="element-5327">
          <div className="flex items-center mb-4" data-id="element-5328">
            <div className="p-2 rounded-full bg-gray-100 dark:bg-gray-700 mr-3" data-id="element-5329">
              <BuildingIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" data-id="element-5330" />
            </div>
            <div data-id="element-5331">
              <h3 className="text-lg font-medium" data-id="element-5332">{merchant.name}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-5333">
                Current Status: {getStatusBadge(merchant.status)}
              </p>
            </div>
          </div>
          <div className="border-b border-gray-200 dark:border-gray-700 pb-2 mb-4" data-id="element-5334">
            <h4 className="font-medium text-gray-700 dark:text-gray-300" data-id="element-5335">
              Complete Status History
            </h4>
          </div>
          <div className="space-y-3 max-h-96 overflow-y-auto" data-id="element-5336">
            {merchant.statusHistory.map((history, index) => <div key={index} className="bg-gray-50 dark:bg-gray-800 p-3 rounded-md" data-id="element-5337">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center" data-id="element-5338">
                  <div className="flex items-center mb-2 sm:mb-0" data-id="element-5339">
                    {history.previousStatus ? <div className="flex items-center" data-id="element-5340">
                        <span className="inline-block" data-id="element-5341">
                          {getStatusBadge(history.previousStatus)}
                        </span>
                        <ChevronRightIcon className="h-4 w-4 mx-2 text-gray-500 dark:text-gray-400" data-id="element-5342" />
                        <span className="inline-block" data-id="element-5343">
                          {getStatusBadge(history.newStatus)}
                        </span>
                      </div> : <div className="flex items-center" data-id="element-5344">
                        <span className="text-sm text-gray-500 dark:text-gray-400 mr-2" data-id="element-5345">
                          Initial status:
                        </span>
                        <span className="inline-block" data-id="element-5346">
                          {getStatusBadge(history.newStatus)}
                        </span>
                      </div>}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-5347">
                    {formatDateTime(history.changedAt)}
                  </div>
                </div>
                <div className="mt-2 text-sm text-gray-700 dark:text-gray-300" data-id="element-5348">
                  <span className="font-medium" data-id="element-5349">Reason:</span> {history.reason}
                </div>
                <div className="mt-1 text-xs text-gray-500 dark:text-gray-400" data-id="element-5350">
                  Changed by: {history.changedBy}
                </div>
              </div>)}
            {merchant.statusHistory.length === 0 && <div className="text-center py-4 text-gray-500 dark:text-gray-400" data-id="element-5351">
                No status change history available.
              </div>}
          </div>
        </div>
      </Modal>
    </div>;
};
export default MerchantDetails;